import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Crown, Globe, Rocket, Sparkles } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import {
  money,
  planLabel,
  PLAN_PRICES,
  PLAN_INCLUDED_LEADS,
  UPGRADE_PERKS,
  upgradeTargets,
  planTermNote,
} from '@/data/constants';
import { track } from '@/lib/tracking';

interface Props {
  /** Current plan code, or null/undefined for no membership. */
  currentPlan?: string | null;
  /** Where the banner is shown (analytics). */
  placement: string;
  /** Compact = one row per plan (dashboard). Full = cards (membership page). */
  compact?: boolean;
}

/**
 * UPGRADE OFFERS — Pro ($199 / 10 leads) and Premium ($499 / 25 leads,
 * FREE website, Limited Time Offer). Prices and lead counts come from
 * PLAN_PRICES / PLAN_INCLUDED_LEADS; the copy from UPGRADE_PERKS.
 *
 * Every CTA opens that plan's real hosted checkout via the existing Pricing
 * deep link (?plan=pro | ?plan=premium). Credits change only when the signed
 * payment webhook confirms the upgrade — never from this button.
 */
const UpgradeOffers: React.FC<Props> = ({ currentPlan, placement, compact }) => {
  const { lang, path } = useI18n();
  const es = lang === 'es';
  const targets = upgradeTargets(currentPlan);
  if (!targets.length) return null;

  const currentLeads = currentPlan ? PLAN_INCLUDED_LEADS[currentPlan] || 0 : 0;

  return (
    <section aria-label={es ? 'Mejore su plan' : 'Upgrade your plan'} className="rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50 via-white to-amber-50 p-4 sm:p-6">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-500 text-white">
          <Rocket className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h2 className="text-base font-extrabold text-slate-900 sm:text-lg">
            {currentPlan
              ? es ? 'Consiga más leads cada mes' : 'Get more leads every month'
              : es ? 'Elija el plan que le trae más trabajo' : 'Pick the plan that brings you more work'}
          </h2>
          {currentLeads > 0 && (
            <p className="mt-0.5 text-sm text-slate-600">
              {es
                ? `Hoy recibe ${currentLeads} leads al mes. Suba de plan y reciba hasta ${PLAN_INCLUDED_LEADS.premium}.`
                : `You get ${currentLeads} leads a month today. Upgrade and get up to ${PLAN_INCLUDED_LEADS.premium}.`}
            </p>
          )}
        </div>
      </div>

      <div className={`mt-4 grid gap-3 ${compact ? '' : 'md:grid-cols-2'}`}>
        {targets.map((code) => {
          const premium = code === 'premium';
          const perks = UPGRADE_PERKS[code];
          const href = `${path('/pricing')}?plan=${code}`;
          return (
            <div
              key={code}
              className={`relative flex flex-col rounded-xl border-2 bg-white p-4 ${premium ? 'border-slate-900' : 'border-orange-300'}`}
            >
              {perks.badge && (
                <span className="absolute -top-3 left-4 inline-flex items-center gap-1 rounded-full bg-red-600 px-2.5 py-0.5 text-[11px] font-black uppercase tracking-wide text-white">
                  <Sparkles className="h-3 w-3" /> {es ? perks.badge.es : perks.badge.en}
                </span>
              )}
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="flex items-center gap-1.5 text-lg font-black text-slate-900">
                  {premium ? <Crown className="h-5 w-5 text-amber-500" /> : null}
                  {planLabel(code, lang)}
                </p>
                <p className="text-lg font-black text-slate-900">
                  {money(PLAN_PRICES[code])}
                  <span className="text-xs font-semibold text-slate-500">{es ? '/mes' : '/mo'}</span>
                </p>
              </div>
              <p className="mt-1 text-2xl font-black text-orange-600">
                {PLAN_INCLUDED_LEADS[code]} <span className="text-sm font-bold text-slate-700">{es ? 'leads al mes' : 'leads per month'}</span>
              </p>
              {premium && (
                <p className="mt-2 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-extrabold text-emerald-800">
                  <Globe className="h-4 w-4 shrink-0" /> {es ? 'Sitio web GRATIS incluido' : 'FREE website included'}
                </p>
              )}
              <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
                {(es ? perks.es : perks.en).map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {p}
                  </li>
                ))}
              </ul>
              {premium && (
                <p className="mt-3 text-[11px] leading-relaxed text-slate-500">{planTermNote(code, PLAN_PRICES[code], lang)}</p>
              )}
              <Link
                to={href}
                onClick={() => track('cta_click', { action: 'upgrade_click', plan: code, from: currentPlan || 'none', placement })}
                className={`mt-4 flex min-h-[48px] items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold text-white ${premium ? 'bg-slate-900 hover:bg-slate-800' : 'bg-orange-500 hover:bg-orange-600'}`}
              >
                {currentPlan
                  ? es ? `Subir a ${planLabel(code, lang)}` : `Upgrade to ${planLabel(code, lang)}`
                  : es ? `Elegir ${planLabel(code, lang)}` : `Choose ${planLabel(code, lang)}`}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default UpgradeOffers;
